import { useRef, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useProperty, useSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useCtaSheenStyles } from '../hooks/useCtaSheenStyles';
import { parseBackgroundVideo } from '../utils/heroVideoUtils';

const featuredImageDef: SlotGroupDefinition = {
  label: 'Background Media',
  fields: {
    video_url: {
      label: 'Background Video URL',
      type: 'url',
      defaultField: 'custom_property_featured_video',
      description: 'Optional. Vimeo, YouTube, or direct MP4/WebM. Falls back to image when empty.',
    },
    image: {
      label: 'Fallback / Poster Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Property hero image',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Availability',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

const railScheduleDef: SlotGroupDefinition = {
  label: 'Rail · Schedule a Visit',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Visit' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const railResidentDef: SlotGroupDefinition = {
  label: 'Rail · Resident Access',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const railApplyDef: SlotGroupDefinition = {
  label: 'Rail · Apply Now',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/apply-now' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const railTourDef: SlotGroupDefinition = {
  label: 'Rail · Virtual Tour',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/virtual-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Virtual Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

function RailCalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <rect x="3" y="4.5" width="18" height="16" rx="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3 9h18M8 3v3M16 3v3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7 13h2M11 13h2M15 13h2M7 16.5h2M11 16.5h2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function RailResidentIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <path d="M13 4.5h5a1.5 1.5 0 011.5 1.5v12a1.5 1.5 0 01-1.5 1.5h-5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10 8l4 4-4 4M14 12H3.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function RailApplyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <path d="M9 11V5.5a1.5 1.5 0 013 0V11" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 11V8.5a1.5 1.5 0 013 0V11M15 11v-1a1.5 1.5 0 013 0v5.5a4.5 4.5 0 01-4.5 4.5h-2.2a4 4 0 01-3-1.35l-3-3.4a1.5 1.5 0 012.2-2l1.3 1.4V11" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function RailTourIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="8.5" cy="10" r="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6 18l4.5-4.5 3 3L16 14l5 5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

interface RailLink {
  url?: string;
  label?: string;
  _target?: string;
}

function RailTile({
  link,
  defaultUrl,
  delayClass,
  label,
  icon,
}: {
  link: RailLink;
  defaultUrl: string;
  delayClass: string;
  label: string;
  icon: ReactNode;
}) {
  return (
    <a
      href={link.url || defaultUrl}
      target={link._target || '_self'}
      className={`hero-rail-tile ${delayClass}`}
    >
      <span className="hero-rail-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="hero-rail-label">{link.label || label}</span>
    </a>
  );
}

interface HeroActionRailProps {
  /** `full` = home hero (4 tiles). `schedule-only` = interior hero (Schedule a Visit). */
  variant?: 'full' | 'schedule-only';
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

function HeroActionRail({ variant = 'full' }: HeroActionRailProps) {
  const railSchedule = useSlotGroup('hero.rail_schedule', railScheduleDef);
  const railResident = useSlotGroup('hero.rail_resident', railResidentDef);
  const railApply = useSlotGroup('hero.rail_apply', railApplyDef);
  const railTour = useSlotGroup('hero.rail_tour', railTourDef);

  const property = useProperty() as { virtual_tours?: VirtualTour[] } | null;
  const hasVirtualTours = (property?.virtual_tours || []).some((t) => t.virtual_tour_url);
  const railTourLink = useMemo(
    () =>
      hasVirtualTours
        ? railTour
        : { ...railTour, url: '/gallery', label: 'View Gallery' },
    [hasVirtualTours, railTour],
  );

  const scheduleOnly = variant === 'schedule-only';

  return (
    <>
      <aside
        className={`hero-rail hidden lg:flex${scheduleOnly ? ' hero-rail--schedule-only' : ''}`}
        aria-label="Quick actions"
      >
        <div className="hero-rail-hamburger-spacer" aria-hidden="true" />

        <EditableSlotGroup groupPath="hero.rail_schedule" definition={railScheduleDef} className="hero-rail-cell">
          <RailTile
            link={railSchedule}
            defaultUrl="/schedule-a-tour"
            delayClass="hero-rail-tile-1"
            label="Schedule a Visit"
            icon={<RailCalendarIcon />}
          />
        </EditableSlotGroup>

        {!scheduleOnly && (
          <>
            <EditableSlotGroup groupPath="hero.rail_resident" definition={railResidentDef} className="hero-rail-cell">
              <RailTile
                link={railResident}
                defaultUrl="/resident-access"
                delayClass="hero-rail-tile-2"
                label="Resident Access"
                icon={<RailResidentIcon />}
              />
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="hero.rail_apply" definition={railApplyDef} className="hero-rail-cell">
              <RailTile
                link={railApply}
                defaultUrl="/apply-now"
                delayClass="hero-rail-tile-3"
                label="Apply Now"
                icon={<RailApplyIcon />}
              />
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="hero.rail_tour" definition={railTourDef} className="hero-rail-cell">
              <RailTile
                link={railTourLink}
                defaultUrl={hasVirtualTours ? '/virtual-tour' : '/gallery'}
                delayClass="hero-rail-tile-4"
                label={hasVirtualTours ? 'Virtual Tour' : 'View Gallery'}
                icon={<RailTourIcon />}
              />
            </EditableSlotGroup>
          </>
        )}
      </aside>

      <style>{heroRailStyles}</style>
    </>
  );
}

const heroRailStyles = `
  .hero-rail {
    position: absolute;
    top: var(--offer-banner-height, 0px);
    right: 0;
    bottom: 0;
    z-index: 20;
    width: var(--hero-rail-width);
    max-width: var(--hero-rail-width);
    flex-direction: column;
  }
  .hero-rail-hamburger-spacer {
    flex: 1 1 0;
    min-height: 0;
    width: 100%;
    max-width: var(--hero-rail-width);
    max-height: var(--hero-rail-hamburger-height, var(--hero-rail-width));
  }
  /* Interior heroes: two fixed square slots (hamburger + Schedule a Visit)
     pinned to the top-right — same tile size as the home hero hamburger. */
  .hero-rail--schedule-only {
    bottom: auto;
    height: auto;
  }
  .hero-rail--schedule-only .hero-rail-hamburger-spacer,
  .hero-rail--schedule-only .hero-rail-cell {
    flex: none;
    height: var(--hero-rail-hamburger-height, var(--hero-rail-width));
    min-height: var(--hero-rail-hamburger-height, var(--hero-rail-width));
  }
  .hero-rail-cell {
    flex: 1 1 0;
    min-height: 0;
    display: flex;
  }
  .hero-rail-tile {
    flex: 1 1 0;
    width: 100%;
    max-width: var(--hero-rail-width);
    min-height: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.85rem;
    padding: 1rem 0.75rem;
    text-align: center;
    text-decoration: none;
    color: var(--vibe-color-text-on-primary, #fff);
    background: color-mix(in srgb, var(--vibe-color-primary) 55%, transparent);
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
    border-top: 1px solid rgba(255, 255, 255, 0.14);
    transition: background 0.3s ease;
    animation: heroRailIn 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
  }
  .hero-rail-tile:hover {
    background: color-mix(in srgb, var(--vibe-color-primary) 90%, transparent);
  }
  .hero-rail-tile:focus-visible {
    outline: 2px solid var(--vibe-color-text-on-primary, #fff);
    outline-offset: -3px;
  }
  .hero-rail-icon {
    display: block;
    color: var(--vibe-color-text-on-primary, #fff);
  }
  .hero-rail-icon svg {
    width: 2rem;
    height: 2rem;
  }
  @media (min-width: 1024px) {
    .hero-rail-icon svg { width: 2.25rem; height: 2.25rem; }
  }
  .hero-rail-label {
    font-family: var(--vibe-font-body);
    font-size: 0.7rem;
    line-height: 1.25;
    letter-spacing: 0.12em;
    font-weight: 600;
    text-transform: uppercase;
  }
  @media (min-width: 1024px) {
    .hero-rail-label { font-size: 0.78rem; }
  }
  @keyframes heroRailIn {
    from { opacity: 0; transform: translateX(28px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  .hero-rail-tile-1 {
    animation-delay: 0.45s;
    background: color-mix(in srgb, var(--vibe-color-primary) 68%, transparent);
  }
  .hero-rail-tile-2 {
    animation-delay: 0.6s;
    background: color-mix(in srgb, var(--vibe-color-primary) 56%, transparent);
  }
  .hero-rail-tile-3 {
    animation-delay: 0.75s;
    background: color-mix(in srgb, var(--vibe-color-primary) 44%, transparent);
  }
  .hero-rail-tile-4 {
    animation-delay: 0.9s;
    background: color-mix(in srgb, var(--vibe-color-primary) 32%, transparent);
  }
  @media (prefers-reduced-motion: reduce) {
    .hero-rail-tile { animation: none; opacity: 1; transform: none; }
  }
`;


export function Hero() {
  const bgImage = useSlotGroup('hero.bg_image', featuredImageDef);
  const ctaLink = useSlotGroup('hero.cta_link', ctaLinkDef);
  useCtaSheenStyles();

  // Parallax refs (image-only backgrounds)
  const sectionRef = useRef<HTMLElement>(null);
  const imgWrapRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoReady, setVideoReady] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const videoInfo = useMemo(
    () => parseBackgroundVideo(bgImage.video_url),
    [bgImage.video_url],
  );
  const showVideoBackground = !!videoInfo && !prefersReducedMotion;

  useEffect(() => {
    setPrefersReducedMotion(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  useEffect(() => {
    setVideoReady(false);
    setIframeLoaded(false);
  }, [videoInfo?.src, videoInfo?.type]);

  useEffect(() => {
    if (!showVideoBackground || videoInfo?.type !== 'direct') return;
    const video = videoRef.current;
    if (!video) return;

    const onCanPlay = () => setVideoReady(true);
    video.addEventListener('canplay', onCanPlay);
    if (video.readyState >= 3) setVideoReady(true);

    return () => video.removeEventListener('canplay', onCanPlay);
  }, [showVideoBackground, videoInfo]);

  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="raleway"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Raleway:ital,wght@0,400;0,700;1,400;1,700&display=swap';
    link.setAttribute('data-vibe-font', 'raleway');
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    if (showVideoBackground) return;
    const section = sectionRef.current;
    const imgWrap = imgWrapRef.current;
    if (!section || !imgWrap) return;
    // Respect reduced-motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onScroll = () => {
      const rect = section.getBoundingClientRect();
      // rect.top < 0 once the section has scrolled above the viewport top
      const scrolled = -rect.top;
      // Only compute while the section is at least partially visible
      if (scrolled < -window.innerHeight || scrolled > section.offsetHeight) return;
      // Shift the image down at 30 % of scroll speed, creating the parallax depth
      imgWrap.style.transform = `translateY(${scrolled * 0.3}px)`;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // seed the position on mount (handles pre-scrolled pages)
    return () => window.removeEventListener('scroll', onScroll);
  }, [showVideoBackground]);

  return (
    <section ref={sectionRef} className="hero-section hero-rail-anchor relative w-full overflow-hidden">
      <EditableSlotGroup groupPath="hero.bg_image" definition={featuredImageDef}>
        {showVideoBackground && videoInfo ? (
          <div className="hero-bg-media absolute inset-0 bg-[var(--vibe-color-text)]">
            {bgImage.image && (
              <VibesitesImage
                url={bgImage.image}
                alt={bgImage.alt || ''}
                priority
                sizes="100vw"
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
                  videoInfo.type === 'embed'
                    ? iframeLoaded
                      ? 'opacity-0'
                      : 'opacity-100'
                    : videoReady
                      ? 'opacity-0'
                      : 'opacity-100'
                }`}
              />
            )}
            {videoInfo.type === 'embed' ? (
              <iframe
                src={videoInfo.src}
                className={`hero-video-iframe absolute inset-0 h-full w-full transition-opacity duration-1000 ${
                  iframeLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                allow="autoplay; fullscreen"
                frameBorder="0"
                onLoad={() => setIframeLoaded(true)}
                title="Background video"
              />
            ) : (
              <video
                ref={videoRef}
                src={videoInfo.src}
                autoPlay
                muted
                loop
                playsInline
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
                  videoReady ? 'opacity-100' : 'opacity-0'
                }`}
              />
            )}
          </div>
        ) : (
          /* Parallax + Ken Burns animated background */
          <div
            ref={imgWrapRef}
            className="hero-bg-media absolute h-[140%]"
            style={{ top: '-20%', left: '-1%', right: '-1%', willChange: 'transform' }}
          >
            {bgImage.image ? (
              <VibesitesImage
                url={bgImage.image}
                alt={bgImage.alt || ''}
                priority
                sizes="100vw"
                className="h-full w-full object-cover"
                style={{
                  animation: 'heroKenBurns 30s ease-in-out infinite alternate',
                  willChange: 'transform',
                  transformOrigin: 'bottom right',
                  imageRendering: 'high-quality' as any,
                }}
              />
            ) : (
              <div className="h-full w-full bg-[var(--vibe-color-text)]" />
            )}
          </div>
        )}
      </EditableSlotGroup>

      {/* Gradient overlay for text contrast */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/20" />

      {/* Content — left-aligned stack; extra right padding on md+ reserves
          room for the action rail pinned to the right edge. */}
      <div className="hero-content relative z-10 flex h-full flex-col items-start justify-center px-6 sm:px-8 text-left text-white">
        <RichTextContent
          path="hero.title"
          label="Title"
          defaultField="property_name"
          defaultValue="<p>Property Name</p>"
          as="h1"
          className="hero-entrance hero-entrance-1 hero-title"
        />

        <RichTextContent
          path="hero.subtitle"
          label="Subtitle"
          defaultValue="<p>In The Edge District</p>"
          as="h2"
          className="hero-entrance hero-entrance-2 hero-subtitle"
        />

        <EditableSlotGroup groupPath="hero.cta_link" definition={ctaLinkDef}>
          <a
            href={ctaLink.url || '/floor-plans'}
            target={ctaLink._target || '_self'}
            className="hero-entrance hero-entrance-3 hero-cta vantage-cta-sheen vantage-cta-hero group/cta"
          >
            <span className="relative z-10">{ctaLink.label || 'View Availability'}</span>
          </a>
        </EditableSlotGroup>
      </div>

      <HeroActionRail />

      <style>{`
        @keyframes heroKenBurns {
          0% { transform: scale(1.01) translate(0, 0); }
          100% { transform: scale(1.05) translate(-0.8%, -0.5%); }
        }
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.15s; }
        .hero-entrance-2 { animation-delay: 0.35s; }
        .hero-entrance-3 { animation-delay: 0.55s; }

        /* Full viewport height — svh/dvh for mobile browser chrome. */
        .hero-section {
          /* Contain the hero-content (z-10) and action rail (z-20) in their own
             stacking context so the floating rail buttons don't out-stack
             fixed-position third-party widgets (e.g. chatbots) at the page root. */
          isolation: isolate;
          --hero-rail-slot-count: 5;
          --hero-rail-width: max(
            168px,
            min(280px, calc(100vh / 5))
          );
          --hero-rail-hamburger-height: var(--hero-rail-width);
          height: 100vh;
          height: 100svh;
          height: 100dvh;
          min-height: 100vh;
          min-height: 100svh;
          min-height: 100dvh;
        }
        @supports (height: 100dvh) {
          .hero-section {
            --hero-rail-width: max(
              168px,
              min(280px, calc(100dvh / 5))
            );
          }
        }

        /* Cover-fit Vimeo / YouTube iframe backgrounds. */
        .hero-video-iframe {
          border: 0;
          pointer-events: none;
          width: 100vw;
          height: 56.25vw;
          min-height: 100%;
          min-width: 177.78vh;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }

        /* Hero copy — matches the reference Vantage home hero (Raleway,
           95px title / 20px subtitle / 16px CTA on desktop). */
        .hero-content {
          padding-left: 1.5rem;
        }
        @media (min-width: 768px) {
          .hero-content {
            padding-left: 4.4rem;
          }
        }
        @media (min-width: 1024px) {
          .hero-content {
            padding-right: var(--hero-rail-width);
          }
        }
        .hero-title {
          font-family: "Raleway", sans-serif;
          font-size: 50px;
          font-weight: 700;
          line-height: 1;
          letter-spacing: 0;
          text-transform: uppercase;
          color: #fff;
          text-shadow: 0 0 12px rgba(255, 255, 255, 0.15), 0 0 24px rgba(255, 255, 255, 0.06);
          margin: 0;
          max-width: 14ch;
        }
        .hero-title p,
        .hero-title span {
          font: inherit;
          color: inherit;
          text-transform: inherit;
          text-shadow: inherit;
          margin: 0;
        }
        /* Tablet: 95px fills the copy column and collides with the action
           rail — step up gradually so desktop keeps the reference size. */
        @media (min-width: 768px) and (max-width: 1023px) {
          .hero-title { font-size: 64px; }
        }
        @media (min-width: 1024px) {
          .hero-title { font-size: 95px; }
        }
        .hero-subtitle {
          font-family: "Raleway", sans-serif;
          font-size: 20px;
          font-weight: 700;
          line-height: 1.2;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #fff;
          text-shadow: 0 0 12px rgba(255, 255, 255, 0.15), 0 0 24px rgba(255, 255, 255, 0.06);
          margin: 0.35em 0 0;
          max-width: min(42rem, 100%);
        }
        .hero-subtitle p,
        .hero-subtitle span {
          font: inherit;
          color: inherit;
          text-transform: inherit;
          text-shadow: inherit;
          letter-spacing: inherit;
          margin: 0;
        }
        /* Tablet: the reserved rail width squeezes the copy column enough to
           push a long subtitle onto a third line — tighten tracking/size so it
           holds to two lines. Mobile (<768) and desktop (>=1024) are unaffected. */
        @media (min-width: 768px) and (max-width: 1023px) {
          .hero-subtitle {
            font-size: 17px;
            letter-spacing: 0.1em;
          }
        }
        .hero-cta {
          max-width: 258px;
          width: 100%;
          margin-top: 30px;
        }
        @media (min-width: 480px) {
          .hero-cta { width: auto; }
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultField: 'property_name',
      defaultValue: '<p>Property Name</p>',
    },
    'hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>In The Edge District</p>',
    },
  },
  slotGroups: {
    'hero.bg_image': featuredImageDef,
    'hero.cta_link': ctaLinkDef,
    'hero.rail_schedule': railScheduleDef,
    'hero.rail_resident': railResidentDef,
    'hero.rail_apply': railApplyDef,
    'hero.rail_tour': railTourDef,
  },
};

(Hero as any).contentSchema = contentSchema;

export default Hero;
